import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { callEndpoint } from 'redux/global';
import { RootState } from 'redux/store';
import { IResponse } from 'types';
import {
  fetchDbDataWithCache,
  getDbCacheContextFromState,
} from 'utils/dbIndexedCache';

export type User = {
  /** Staff row id from the database. */
  id?: string,
  email: string,
  username: string,
  permission: string,
  photo?: string | null,
  createdAt?: string,
  /** Discrete page edit grants (Associate Manager / Manager only). */
  discreteAccess?: DiscreteAccess | null,
}

/** Extensible set of page-level edit grants stored on staff.discrete_access. */
export type DiscreteAccessKey = 'inventoryEdit' | 'departmentsEdit' | 'crmEdit';
export type DiscreteAccess = Partial<Record<DiscreteAccessKey, boolean>>;

export const emptyDiscreteAccess = (): DiscreteAccess => ({
  inventoryEdit: false,
  departmentsEdit: false,
  crmEdit: false,
});

export const MAX_STAFF_PER_HOTEL = 4;

export const getMaxStaffForHotel = (maxStaff?: number | null) => (
  Number(maxStaff) > 0 ? Math.floor(Number(maxStaff)) : MAX_STAFF_PER_HOTEL
);

export type RoomType = {
  name: string,
  id: string,
  adults: number,
  children: number,
  infants: number,
  img: string,
  imgs: string[],
  coRoomTypeId: string
}

export type DateChange = {
  start: string,
  end: string,
  rate: string
}

export type Rate = {
  id: string,
  name: string,
  baseRate: string,
  weekendRate: string,
  dates: DateChange[],
  occupancy: number,
  mealType: ('Breakfast' | 'Lunch' | 'Dinner')[],
  roomTypeId: string,
  coRateId: string
}

export type Services = {
  houseKeeping: {
    date: Date,
    isServed: boolean
  },
  meals: {
    date: Date,
    ids: string[],
    priorIds: string[]
  }
}

export type Card = {
  id: string;
  brand: string;
  last4: string;
  exp: string;
  isDefault?: boolean;
};

export type Discount = {
  percentage: number; // 3 is minumum percentage for discount to be applied
  noOfNights: 'All' | number;
  noOfBookingsToApply: number;
};

export type CRMCampaign = {
  id: string;
  campaignName: string;
  startDate: Date;
  endDate: Date;
  emailMessage: string;
  type: 'Discount Offer' | 'Event invitation' | 'Promotions',
  discountOfferPercentage?: number,
  noOfDiscountNights?: 'All' | number;
  noOfBookingsToApply?: 1,
  graphics?: string; // 1 image, max 30 MB. served from GCS at https://storage.googleapis.com/images_wiegfbiwudjdvh/<fileId>
  /** GCP Cloud Task names for this campaign's scheduled email sends. Managed by the backend. */
  taskNames?: string[];
  /** Keys of schedule-points already sent (`${campaignId}|${year}|${label}`). Backend-managed. */
  sentDateKeys?: string[];
}

export type CRMTabs = {
  discount: Discount;
  campaigns: {
    appliedIds: string[];
    info: CRMCampaign[];
    campaignEmailUsage: { date: string; sent: number; limit?: number } | null,
  }
  settings: {
    emails: {
      lodging: string;
      promotions: string;
      events: string;
      discounts: string;
    }
  }
};

export type Customer = {
  /** Customer row id from the database. */
  id?: string;
  email?: string;
  name: string;
  number?: string;
  whatsapp?: string;
  /** Telegram username without leading @. */
  telegram?: string;
  createdAt?: string;
  noOfBookings?: number;
  lifetimeBookings?: number
  assignedDiscount?: Discount | null;
  /** GCS object id in docs_tcdghgcfycdtycgdc bucket */
  idDocUrl?: string | null;
  /**
   * Guest face (GCS images bucket id or http/data URL).
   * Stored on ID upload so CRM/audits skip MediaPipe.
   */
  facePhoto?: string | null;
  feedback?: {
    ratingInfo: {
      roomId: string,
      rating: 0 | 1 | 2 | 3 | 4,
      date?: string
    }[]
    messages: {
      message: string,
      subject: string,
      messageId: string,
      threadId: string,
      type: 'lodging' | 'promotions' | 'events' | 'discounts'
      roomId?: string, // this exists only if it is the first email message in the message-id thread
      staffEmail?: string;
      createdAt: string
    }[];
  };
};

export type SettingsParts = {
  users: User[] | null,
  groups: string[],
  roomTypes: RoomType[],
  rates: Rate[],
  billing: Card[]
}

type SettingsState = {
  tmpRates: Rate[],
  parts: SettingsParts,
  customers: Customer[],
  crmSettings: CRMTabs,
}

const earlyToday = new Date();
earlyToday.setHours(0, 0, 0, 0);

export const defaultServices: Services = {
  houseKeeping: {
    date: earlyToday,
    isServed: false
  },
  meals: {
    date: earlyToday,
    ids: [],
    priorIds: []
  }
};

const now = new Date();
const day = now.getDay();
// 6 = Saturday, 0 = Sunday
const daysUntilSaturday = (6 - day + 7) % 7 || 7; // If today is Saturday, go to next Saturday
const nearestSaturday = new Date(now);
nearestSaturday.setDate(now.getDate() + daysUntilSaturday);
nearestSaturday.setHours(0, 0, 0, 0);

const nearestSunday = new Date(nearestSaturday);
nearestSunday.setDate(nearestSaturday.getDate() + 1); // Sunday after selected Saturday
nearestSunday.setHours(23, 59, 59, 999);

export const defaultCRMSettings: CRMTabs = {
  discount: {
    percentage: 3, // 3 is minumum percentage for discount to be applied
    noOfNights: 'All',
    noOfBookingsToApply: 0,
  },
  campaigns: {
    appliedIds: [],
    campaignEmailUsage: null,
    info: [
      {
        id: 'weekend-event-uuid',
        campaignName: 'Weekend Events',
        startDate: nearestSaturday,
        endDate: nearestSunday, // Sunday after selected Saturday
        emailMessage: 'Enjoy a special weekend event with top artists at our hotel!',
        type: 'Event invitation',
      },
      {
        id: 'newyear-discount-uuid',
        campaignName: 'New Year Discount',
        startDate: new Date(new Date().setMonth(11, 25)), // Dec 25th
        endDate: new Date(new Date().setMonth(0, 5)), // Jan 5th
        emailMessage: 'Ring in the New Year with a special discount at our hotel! Celebrate with us and enjoy great savings on your stay.',
        type: 'Discount Offer',
        discountOfferPercentage: 5,
        noOfDiscountNights: 'All',
        noOfBookingsToApply: 1
      },
    ]
  },
  settings: {
    emails: {
      lodging: '',
      promotions: '',
      events: '',
      discounts: '',
    }
  }
};

const initialState: SettingsState = {
  tmpRates: [],
  parts: {
    users: null,
    groups: [],
    roomTypes: [],
    rates: [],
    billing: []
  },
  customers: [],
  crmSettings: defaultCRMSettings,
};

export const counterSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setUsers: (state, action: PayloadAction<User[] | null>) => {
      state.parts.users = action.payload;
    },
    setGroups: (state, action: PayloadAction<string[]>) => {
      state.parts.groups = action.payload;
    },
    setRoomTypes: (state, action: PayloadAction<RoomType[]>) => {
      state.parts.roomTypes = action.payload;
    },
    setRates: (state, action: PayloadAction<Rate[]>) => {
      state.parts.rates = action.payload;
    },
    setTmpRates: (state, action: PayloadAction<Rate[]>) => {
      state.tmpRates = action.payload;
    },
    setSettingsParts: (state, action: PayloadAction<SettingsParts>) => {
      state.parts = action.payload;
    },
    setBilling: (state, action: PayloadAction<Card[]>) => {
      state.parts.billing = action.payload;
    },
    setCustomers: (state, action: PayloadAction<Customer[]>) => {
      state.customers = Array.isArray(action.payload) ? action.payload : [];
    },
    addOrUpdateCustomer: (state, action: PayloadAction<Customer>) => {
      const customer = action.payload;
      const emailLower = (customer.email || '').toLowerCase().trim();
      const numberNorm = (customer.number || '').trim();
      const whatsappNorm = (customer.whatsapp || '').trim();
      const telegramNorm = (customer.telegram || '').trim().replace(/^@+/, '');
      if (!emailLower && !numberNorm && !whatsappNorm && !telegramNorm) return;

      let existingIndex = -1;
      if (emailLower) {
        existingIndex = state.customers.findIndex(
          (c) => (c.email || '').toLowerCase() === emailLower,
        );
      } else if (numberNorm) {
        existingIndex = state.customers.findIndex(
          (c) => (c.number || '').trim() === numberNorm,
        );
      } else if (whatsappNorm) {
        existingIndex = state.customers.findIndex(
          (c) => (c.whatsapp || '').trim() === whatsappNorm,
        );
      } else if (telegramNorm) {
        existingIndex = state.customers.findIndex(
          (c) => (c.telegram || '').trim().replace(/^@+/, '') === telegramNorm,
        );
      }

      if (existingIndex > -1) {
        const existing = state.customers[existingIndex];
        const incomingMessages = customer.feedback?.messages;
        const existingMessages = existing.feedback?.messages || [];

        if (incomingMessages?.length) {
          const byId = new Map(existingMessages.map((m) => [m.messageId, m]));
          incomingMessages.forEach((m) => {
            if (m.messageId) byId.set(m.messageId, m);
          });
          const mergedMessages = [...byId.values()].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
          state.customers[existingIndex] = {
            ...existing,
            ...customer,
            feedback: {
              ratingInfo: customer.feedback?.ratingInfo ?? existing.feedback?.ratingInfo ?? [],
              messages: mergedMessages,
            },
          };
        } else {
          state.customers[existingIndex] = { ...existing, ...customer };
        }
      } else {
        state.customers.push(customer);
      }

      if (emailLower) {
        let kept = false;
        state.customers = state.customers.filter((c) => {
          if ((c.email || '').toLowerCase() !== emailLower) return true;
          if (!kept) {
            kept = true;
            return true;
          }
          return false;
        });
      }
    },
    setCRMSettings: (state, action: PayloadAction<CRMTabs>) => {
      state.crmSettings = action.payload;
    }
  },
});

export const {
  setUsers, setGroups, setRoomTypes, setRates, setTmpRates,
  setSettingsParts, setBilling, setCustomers, addOrUpdateCustomer,
  setCRMSettings
} = counterSlice.actions;

export const selectCampaignEmailUsage = (
  state: RootState,
) => state.settings.crmSettings?.campaigns?.campaignEmailUsage;
export const selectUsers = (state: RootState) => state.settings.parts.users;
export const selectGroups = (state: RootState) => state.settings.parts.groups;
export const selectRoomTypes = (state: RootState) => state.settings.parts.roomTypes as RoomType[];
export const selectRates = (state: RootState) => state.settings.parts.rates;
export const selectTmpRates = (state: RootState) => state.settings.tmpRates;
export const selectBilling = (state: RootState) => state.settings.parts.billing;
export const selectSettingsParts = (state: RootState) => state.settings.parts;
export const selectCustomers = (state: RootState) => state.settings.customers;
export const selectCRMSettings = (state: RootState) => state.settings.crmSettings;

export default counterSlice.reducer;

export type Details = {
  phoneNumber: string,
  user: string
}

export const rolesMain1 = [
  'Worker',
  'Cashier',
  'Front Desk I',
  'Front Desk II',
  'Associate Manager',
  'Manager',
  'Owner',
  'Tech team',
] as const;
export const rolesMain = [
  'Worker',
  'Cashier',
  'Front Desk I',
  'Front Desk II',
  'Associate Manager',
  'Manager',
  'Owner',
] as const;
export type Roles = typeof rolesMain1[number];

/** Permission index for each role in `rolesMain1`. */
export const PERM = {
  WORKER: 0,
  CASHIER: 1,
  FRONT_DESK: 2,
  FRONT_DESK_2: 3,
  /** Associate Manager — restricted peer-level manager access */
  SUB_MANAGER: 4,
  /** Manager — former Associate Manager II access */
  MANAGER: 5,
  OWNER: 6,
  TECH: 7,
} as const;

/** Roles that can receive optional Inventory / Departments / CRM edit toggles. */
export const canGrantDiscreteAccess = (
  permission: number | null | undefined,
): boolean => {
  const p = permission || 0;
  return p === PERM.SUB_MANAGER || p === PERM.MANAGER;
};

export const normalizeDiscreteAccess = (
  permission: number,
  access?: DiscreteAccess | null,
): DiscreteAccess => {
  if (!canGrantDiscreteAccess(permission)) {
    return emptyDiscreteAccess();
  }
  return {
    inventoryEdit: Boolean(access?.inventoryEdit),
    departmentsEdit: Boolean(access?.departmentsEdit),
    crmEdit: Boolean(access?.crmEdit),
  };
};

/** Owner+ always edit; Associate Manager / Manager only with explicit grant. */
export const hasDiscreteEdit = (
  permission: number | null | undefined,
  discreteAccess: DiscreteAccess | null | undefined,
  key: DiscreteAccessKey,
): boolean => {
  const p = permission || 0;
  if (p >= PERM.OWNER) return true;
  if (!canGrantDiscreteAccess(p)) return false;
  return Boolean(discreteAccess?.[key]);
};

/** Inventory page: Associate Manager and above can view. Edit still uses `hasDiscreteEdit`. */
export const canAccessInventoryPage = (
  permission: number | null | undefined,
  _discreteAccess?: DiscreteAccess | null,
): boolean => {
  const p = permission || 0;
  return p >= PERM.SUB_MANAGER;
};

/** Departments page: Associate Manager (4) and above. Cashiers use Sales/Refunds sidebar tabs. */
export const canAccessDepartmentsPage = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.SUB_MANAGER;

/** Cashier-only sidebar Sales / Refunds tabs. */
export const canAccessCashierSalesTabs = (
  permission: number | null | undefined,
): boolean => (permission || 0) === PERM.CASHIER;

/** Sales & Refunds capability: Cashiers, or Associate Manager (4) and above. */
export const canAccessSalesRefunds = (
  permission: number | null | undefined,
): boolean => {
  const p = permission || 0;
  return p === PERM.CASHIER || p >= PERM.SUB_MANAGER;
};

/** Room charges: Front Desk I and above (add / pay). */
export const canHandleRoomCharge = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.FRONT_DESK;

/** Adjust room charge amount: Front Desk II and above. */
export const canAdjustRoomCharge = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.FRONT_DESK_2;

/** Delete room charges: Front Desk II and above. */
export const canDeleteRoomCharge = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.FRONT_DESK_2;

/** Dashboard / Metrics: Manager and above. */
export const canAccessDashboard = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.MANAGER;

export const canAccessMetrics = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.MANAGER;

/** CRM: Associate Manager and above (same view threshold as Inventory). */
export const canAccessCrm = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.SUB_MANAGER;

/** Audit type filters / selector: Associate Manager and above. */
export const canUseAuditTools = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.SUB_MANAGER;

/** See every staff member's audits (Manager+). */
export const canViewAllAudits = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.MANAGER;

/** Associate Manager: peer audits at their level and below. */
export const isAssociateManager = (
  permission: number | null | undefined,
): boolean => (permission || 0) === PERM.SUB_MANAGER;

/**
 * Occupancy (and similar) types Associate Managers can always view —
 * ignore higher-role author gate. Extend this list when more types need the same.
 */
export const ASSOCIATE_UNRESTRICTED_LOG_TYPES = [
  'Check in',
  'Check out',
] as const;

export const isAssociateUnrestrictedLogType = (
  type?: string | null,
): boolean => (
  Boolean(type && (ASSOCIATE_UNRESTRICTED_LOG_TYPES as readonly string[]).includes(type))
);

/** @deprecated Use isAssociateManager */
export const isAssociateManagerI = isAssociateManager;

/** Owner+: modify any audit (former Manager-tier modify removed with role). */
export const canModifyAllAudits = (
  permission: number | null | undefined,
): boolean => (permission || 0) >= PERM.OWNER;

export const getInfo = (forceRefresh = false) => async (
  dispatch: Function,
  getState: Function,
): Promise<IResponse> => {
  const ctx = getDbCacheContextFromState(getState());
  return fetchDbDataWithCache(ctx, {
    table: 'info',
    forceRefresh,
    fetchFromBe: () => dispatch(
      callEndpoint({
        api: 'info',
        method: 'GET',
      }),
    ),
  });
};

export const saveNumbers = (body: Details[]) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'savenumbers',
      body: { numbers: body },
      method: 'PATCH',
    }),
  );
  return res;
};

export const setEmailRec =
(emailRec: string | null) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'setemailreceiver',
      body: { emailRec },
      method: 'PATCH',
    }),
  );
  return res;
};

type User2 = {
  path: string
} & User;

export const addStaff = (body: User2) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addstaff',
      body,
      method: 'POST',
    }),
  );
  return res;
};

export const editStaff = (
  body: Partial<User> & { email: string },
) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'editstaff',
      body,
      method: 'PATCH',
    }),
  );
  return res;
};

export const deleteStaff = (email: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletestaff',
      body: { email },
      method: 'DELETE',
    }),
  );
  return res;
};

export const saveDisplayNumber =
(displayNumber: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'savedisplaynumber',
      body: { displayNumber },
      method: 'PATCH',
    }),
  );
  return res;
};

export const saveCurrency =
(newCurrency: string) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'savecurrency',
      body: { newCurrency },
      method: 'PATCH',
    }),
  );
  return res;
};

export const saveHotelPrefs =
(prefsPatch: Record<string, unknown>) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'savehotelprefs',
      body: { prefsPatch },
      method: 'PATCH',
    }),
  );
  return res;
};

export const saveHotelProfile =
(profile: Record<string, string>) => async (dispatch: Function): Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'savehotelprofile',
      body: { profile },
      method: 'PATCH',
    }),
  );
  return res;
};

type Subscribe = {
  expires: string,
  channelExpiry: string | undefined,
  billingDate: string,
}

export const subscribe = (s: Subscribe) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'subscribe',
      body: { ...s },
      method: 'PATCH',
    }),
  );
  return res;
};

export const verifyPayment = (trans: Record<string, any>) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'verifypayment',
      method: 'POST',
      body: { trans }
    }),
  );
  return res;
};

type ExtendSub = {
  expires: string,
  channelExpiry: string | undefined,
  billingDate: string,
}

type ExtendSub1 = {
  keyCardExpiry: string
}

export const extendSubscription = (es: ExtendSub | ExtendSub1) => async (dispatch: Function)
  : Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'extendsubscription',
      method: 'PATCH',
      body: { ...es }
    }),
  );
  return res;
};

type AddGroup = {
  addGroup: string,
}

export const addGroup = (ag: AddGroup) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addgroup',
      body: { ag },
      method: 'PATCH',
    }),
  );
  return res;
};

type UpdateRoomTypes = {
  roomTypes: RoomType[],
  roomType?: RoomType,
  isUpdate?: boolean,
  isDelete?: boolean
}

export const updateRoomType = (updateRoomT: UpdateRoomTypes) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'updateroomtypes',
      body: { updateRoomT },
      method: 'PATCH',
    }),
  );
  return res;
};

type UpdateRates = {
  rates: Rate[],
  rate?: Rate,
  isUpdate?: true,
  isDelete?: true,
};

export const updateRates = (ratesBody: UpdateRates) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'updaterates',
      body: { ratesBody },
      method: 'PATCH',
    }),
  );
  return res;
};

type AddPhotos = {
  photos: string[],
  roomTypeName?: string,
  isNoLog?: boolean,
  signal?: AbortSignal,
}

export const addPhotos = (ap: AddPhotos) => async (dispatch: Function)
: Promise<IResponse> => {
  const { signal, ...body } = ap;
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addphotos',
      body,
      method: 'POST',
      checkLimit: 'restrictions',
      signal,
    }),
  );
  return res;
};

type DeletePhoto = {
  ids: string[],
  roomTypeName: string,
  isNoLog?: boolean,
  signal?: AbortSignal,
}

export const deletePhoto = (dp: DeletePhoto) => async (dispatch: Function)
: Promise<IResponse> => {
  const { signal, ...body } = dp;
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletephoto',
      body,
      method: 'DELETE',
      signal,
    }),
  );
  return res;
};

export const uploadCampaignGraphic = (graphic: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'uploadcampaigngraphic',
      body: { graphic },
      method: 'POST',
    }),
  );
  return res;
};

export const deleteCampaignGraphic = (fileId: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deletecampaigngraphic',
      body: { fileId },
      method: 'DELETE',
    }),
  );
  return res;
};

export type RoomTypeCO = {
  roomCount: number,
  roomType: RoomType
}

export const addRoomTypeCO = (art: RoomTypeCO) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addroomtypeco',
      body: { ...art },
      method: 'POST',
      checkLimit: 'both'
    }),
  );
  return res;
};

export const updateRoomTypeCO = (urt: RoomTypeCO) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'updateroomtypeco',
      body: { ...urt },
      method: 'PUT',
      checkLimit: 'availability'
    }),
  );
  return res;
};

export const deleteRoomTypeCO = (coRoomTypeId: string) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deleteroomtypeco',
      body: { coRoomTypeId },
      method: 'DELETE'
    }),
  );
  return res;
};

export type RatePlanCO = {
  roomType: RoomType | undefined,
  ratePlan: Rate | undefined
}

export const addRatePlanCO = (arp: RatePlanCO) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'addrateplanco',
      body: { ...arp },
      method: 'POST',
      checkLimit: 'restrictions'
    }),
  );
  return res;
};

export const updateRatePlanCO = (urp: RatePlanCO) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'updaterateplanco',
      body: { ...urp },
      method: 'PUT',
      checkLimit: 'restrictions'
    }),
  );
  return res;
};

export type DeleteRatePlanCO = Omit<RatePlanCO, 'roomType'>;

export const deleteRatePlanCO = (drp: DeleteRatePlanCO) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'deleterateplanco',
      body: { ...drp },
      method: 'DELETE',
      checkLimit: 'restrictions'
    }),
  );
  return res;
};

export type DateRestriction = {
  start: Date,
  end: Date,
  days?: ('mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su')[]
  rate: number
};

export type RestrictionCO = {
  property_id: string,
  rate_plan_id: string
  date_from: string,
  date_to: string,
  days?: ('mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su')[]
  rate: number
};

export const updateRestrictionsCO = (values: RestrictionCO[]) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'updaterestrictionsco',
      body: { values },
      method: 'POST'
    }),
  );
  return res;
};

export type AvailabilityCO = {
  property_id: string,
  room_type_id: string
  date_from: string,
  date_to: string,
  availability: number
};

export const updateAvailabilityCO = (values: AvailabilityCO[]) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'updateavailabilityco',
      body: { values },
      method: 'POST',
      checkLimit: 'availability'
    }),
  );
  return res;
};

export const saveCRMSettings = (crmSettings: CRMTabs) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'savecrmsettings',
      body: { crmSettings },
      method: 'PATCH',
    }),
  );
  return res;
};

type UpsertCustomer = {
  email?: string;
  name: string;
  number?: string;
  whatsapp?: string;
  telegram?: string;
  noOfBookings?: number;
  lifetimeBookings?: number;
  assignedDiscount?: Discount | null;
  resetDiscount: Discount | null;
  idDocUrl?: string | null;
  facePhoto?: string | null;
};

export type UploadCustomerIdDoc = {
  email?: string;
  number?: string;
  document?: string;
  /** Cropped face data URL from MediaPipe — persisted so CRM can show it. */
  face?: string;
};

export type CustomerIdDocContact = {
  email?: string;
  number?: string;
};

export const uploadCustomerIdDoc = (body: UploadCustomerIdDoc) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'upload-customer-iddoc',
      body,
      method: 'POST',
    }),
  );
  return res;
};

export const getCustomerIdDoc = (contact: CustomerIdDocContact) => async (dispatch: Function)
: Promise<IResponse> => {
  const params = new URLSearchParams();
  if (contact.email) params.set('email', contact.email);
  if (contact.number) params.set('number', contact.number);
  const res: IResponse = await dispatch(
    callEndpoint({
      api: `customer-iddoc?${params.toString()}`,
      method: 'GET',
    }),
  );
  return res;
};

export const upsertCustomer = (body: UpsertCustomer) => async (dispatch: Function)
: Promise<IResponse> => {
  const res: IResponse = await dispatch(
    callEndpoint({
      api: 'upsertcustomer',
      body,
      method: 'POST',
    }),
  );
  return res;
};
